import { type ClassValue, clsx } from "clsx";
import slug from "slug";
import { twMerge } from "tailwind-merge";
import { type CountryCode, parsePhoneNumberFromString } from "libphonenumber-js/min";

export const sleep = (delayMs: number): Promise<void> => {
  return new Promise<void>((resolve) => setTimeout(resolve, delayMs));
};

export const debounce = <TArgs extends readonly unknown[]>(
  delayMs: number,
  cb: (...args: TArgs) => void | Promise<void>,
) => {
  let time = Date.now();

  return function debounced(...args: TArgs) {
    if (time + delayMs - Date.now() < 0) {
      cb(...args);
      time = Date.now();
    }
  };
};

export const range = (count: number | string, start = 0): number[] => {
  const parsedCount = typeof count === "string" ? Number.parseInt(count, 10) : count;
  const safeCount = Number.isFinite(parsedCount) ? Math.max(0, parsedCount) : 0;

  return Array.from({ length: safeCount }, (_, i) => i + start);
};

// eslint-disable-next-line
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const generateSlug = (name: string) => {
  return slug(name, { lower: true });
};

export const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

export const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 2) {
    return digits.slice(0, 2) + "/" + digits.slice(2);
  }
  return digits;
};

export const formatPhone = (value: string) => {
  const numeric = value.replace(/\D/g, "");
  const normalizedDigits =
    numeric.length === 11 && numeric.startsWith("1") ? numeric.slice(1) : numeric;
  const digits = normalizedDigits.slice(0, 10);

  if (digits.length === 0) return "";
  if (digits.length < 3) return digits;
  // Keep 3-digit state as "(123" so backspace can continue naturally
  // instead of getting stuck re-inserting a trailing ')'.
  if (digits.length === 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatCurrency = (amountInCents: number, locale: string, currency: string) => {
  const hasFractionalAmount = amountInCents % 100 !== 0;

  return new Intl.NumberFormat(locale, {
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: hasFractionalAmount ? 2 : 0,
    minimumFractionDigits: hasFractionalAmount ? 2 : 0,
    style: "currency",
  }).format(amountInCents / 100);
};

/**
 * Normalizes a raw phone string to E.164 format.
 * Supports US 10-digit and 11-digit (with leading 1) numbers,
 * as well as numbers already prefixed with "+".
 */
export const normalizePhoneNumberToE164 = (
  rawPhone: string,
  invalidPhoneMessage: string,
  defaultCountry?: string,
): string => {
  const trimmed = rawPhone.trim();

  if (trimmed.startsWith("+")) {
    const cleaned = `+${trimmed.replace(/[^\d]/g, "")}`;
    if (!/^\+\d{8,15}$/.test(cleaned)) {
      throw new Error(invalidPhoneMessage);
    }
    return cleaned;
  }

  if (defaultCountry) {
    const parsedNumber = parsePhoneNumberFromString(trimmed, defaultCountry as CountryCode);
    if (parsedNumber?.isPossible()) {
      return parsedNumber.number;
    }
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;

  throw new Error(invalidPhoneMessage);
};
